/**
 * SignatureModal.tsx
 * V55.30: 서명 캔버스 모달
 *
 * 면담 기록 작성 시 면담자 서명을 캔버스에 그려서 저장하는 모달
 *
 * 기능:
 * - 캔버스에 마우스/터치로 서명 그리기
 * - 취소: 서명 취소 후 모달 닫기
 * - 다시하기: 캔버스 초기화
 * - 저장: 서명을 PNG base64로 export 후 콜백 호출
 */

import React, { useRef, useEffect, useState, useCallback } from 'react';
import { X, RotateCcw, Check } from 'lucide-react';
import { ModalOverlay } from '../../../shared/components/ModalOverlay';

interface SignatureModalProps {
  /** 모달 표시 여부 */
  isOpen: boolean;
  /** 모달 닫기 콜백 */
  onClose: () => void;
  /** 서명 저장 콜백 (base64 PNG data) */
  onSave: (signatureBase64: string) => void;
}

export function SignatureModal({ isOpen, onClose, onSave }: SignatureModalProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  // 캔버스 초기화
  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  }, []);

  // 모달 열릴 때 캔버스 초기화
  useEffect(() => {
    if (isOpen) {
      // 약간의 딜레이 후 초기화 (DOM 렌더링 완료 대기)
      const timer = setTimeout(() => {
        clearCanvas();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen, clearCanvas]);

  // 마우스/터치 좌표 획득
  const getCoordinates = (
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
  ): { x: number; y: number } | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();

    if ('touches' in e) {
      // 터치 이벤트
      const touch = e.touches[0];
      if (!touch) return null;
      return {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top,
      };
    } else {
      // 마우스 이벤트
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    }
  };

  // 그리기 시작
  const startDrawing = (
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
  ) => {
    const coords = getCoordinates(e);
    if (!coords) return;

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;

    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
    setIsDrawing(true);
  };

  // 그리기 중
  const draw = (
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
  ) => {
    if (!isDrawing) return;

    const coords = getCoordinates(e);
    if (!coords) return;

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx) return;

    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#000000';
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
    setHasDrawn(true);
  };

  // 그리기 종료
  const stopDrawing = () => {
    setIsDrawing(false);
  };

  // 서명 저장
  const handleSave = () => {
    const canvas = canvasRef.current;
    if (!canvas || !hasDrawn) {
      alert('서명을 입력해주세요.');
      return;
    }

    // PNG base64 export (data:image/png;base64, 제거)
    const dataUrl = canvas.toDataURL('image/png');
    const base64 = dataUrl.split(',')[1];
    onSave(base64);
    onClose();
  };

  // 취소
  const handleCancel = () => {
    onClose();
  };

  // 다시하기
  const handleReset = () => {
    clearCanvas();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <ModalOverlay
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
        }}
        onClose={handleCancel}
      />

      {/* Modal */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 20px 50px rgba(0, 0, 0, 0.3)',
          zIndex: 1001,
          width: '400px',
          maxWidth: '90vw',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 20px',
            borderBottom: '1px solid #e2e8f0',
          }}
        >
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
            서명 입력
          </h3>
          <button
            onClick={handleCancel}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Canvas Container */}
        <div style={{ padding: '20px' }}>
          <div
            style={{
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              overflow: 'hidden',
              background: '#ffffff',
            }}
          >
            <canvas
              ref={canvasRef}
              width={360}
              height={150}
              style={{
                display: 'block',
                cursor: 'crosshair',
                touchAction: 'none', // 터치 스크롤 방지
              }}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
          </div>
          <p
            style={{
              marginTop: '8px',
              fontSize: '12px',
              color: '#94a3b8',
              textAlign: 'center',
            }}
          >
            위 영역에 서명을 입력하세요
          </p>
        </div>

        {/* Footer Buttons */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
            padding: '16px 20px',
            borderTop: '1px solid #e2e8f0',
          }}
        >
          <button
            onClick={handleCancel}
            style={{
              padding: '8px 16px',
              background: '#f1f5f9',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              color: '#64748b',
            }}
          >
            취소
          </button>
          <button
            onClick={handleReset}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '8px 16px',
              background: '#fef3c7',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              color: '#92400e',
            }}
          >
            <RotateCcw size={14} />
            다시하기
          </button>
          <button
            onClick={handleSave}
            disabled={!hasDrawn}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '8px 16px',
              background: hasDrawn ? '#4f46e5' : '#cbd5e1',
              border: 'none',
              borderRadius: '6px',
              cursor: hasDrawn ? 'pointer' : 'not-allowed',
              fontSize: '14px',
              color: 'white',
            }}
          >
            <Check size={14} />
            저장
          </button>
        </div>
      </div>
    </>
  );
}
